import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  Upload,
  message,
  Radio,
  Row,
  Col,
  Spin
} from "antd";
import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import BrandsServ from "../service/BrandsServ";
import ProductServ from "../service/ProductServ";
import SafeImage, { getImageUrl } from "./SafeImage";

const { Option } = Select;

const AddBrands = ({ open, refreshBrands, brandDetails, isEditing }) => {
  const brandId = brandDetails?._id || brandDetails?.id;
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [productList, setProductList] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState("");
  const [inputType, setInputType] = useState("upload");
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Fetch products
  const fetchProducts = async (page = 1, search = "") => {
    try {
      const response = await ProductServ.index();
      const data = response?.data || response || [];
      setProductList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error("Failed to load products");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Pre-fill on edit
  useEffect(() => {
    if (isEditing && brandDetails) {
      form.setFieldsValue({
        name: brandDetails.name,
        description: brandDetails.description || "",
        imageUrl: brandDetails.imageUrl,
        products:
          brandDetails.products?.map((p) => ({
            productId: p.productId || p._id || p.id,
            description: p.description || "",
          })) || [],
      });
      setImagePreview(brandDetails.imageUrl || "");
      if (brandDetails.imageUrl && (brandDetails.imageUrl.startsWith('http') || brandDetails.imageUrl.startsWith('https') || brandDetails.imageUrl.startsWith('data:'))) {
        setInputType("url");
      } else {
        setInputType("upload");
      }
    } else {
      form.resetFields();
      setImagePreview("");
      setInputType("upload");
    }
  }, [isEditing, brandDetails, form]);

  const handleLogoUpload = async (file) => {
    try {
      setIsUploadingImage(true);
      setImageError("");
      const formData = new FormData();
      formData.append("image", file);
      const res = await BrandsServ.uploadImage(formData);
      if (res && res.imageUrl) {
        const fullUrl = getImageUrl(res.imageUrl);
        setImagePreview(fullUrl);
        form.setFieldsValue({ imageUrl: res.imageUrl });
        message.success("Logo uploaded successfully");
      }
    } catch (error) {
      const errorMsg = error.message || error.response?.data?.message || "Failed to upload image";
      setImageError(errorMsg);
      message.error(errorMsg);
    } finally {
      setIsUploadingImage(false);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      const payload = {
        id: brandId,
        name: values.name,
        description: values.description || "",
        imageUrl: values.imageUrl,
        products: (values.products || []).map((item) => {
          const selectedProduct = productList.find(
            (p) => p.id === item.productId || p._id === item.productId
          );
          return {
            productId: selectedProduct?.id || selectedProduct?._id,
            name: selectedProduct?.name || "",
            price: selectedProduct?.price || "",
            stock: selectedProduct?.stock || "",
            imageUrl: selectedProduct?.imageUrl || "",
            shopId: selectedProduct?.shopId || "",
            description: item.description || "",
          };
        }),
      };

      if (isEditing && brandId) {
        await BrandsServ.update(brandId, payload);
        message.success("Brand updated successfully");
      } else {
        await BrandsServ.create(payload);
        message.success("Brand created successfully");
      }

      handleClose(true);
    } catch (error) {
      console.error("Error saving brand:", error);
      message.error(error.message || "Failed to save brand");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (shouldRefresh) => {
    refreshBrands(shouldRefresh);
    form.resetFields();
    setImagePreview("");
    setInputType("upload");
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 800,
    color: '#0B1F4D',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: 6,
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: 'linear-gradient(135deg, #4776E6 0%, #0B1F4D 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#1a1d2e' }}>
              {isEditing ? "Edit Brand" : "Create Brand"}
            </h2>
            <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 500, marginTop: 1 }}>
              {isEditing ? "Update brand details" : "Standardized Brand & Catalog Management"}
            </p>
          </div>
        </div>
      }
      open={open}
      onCancel={() => handleClose(false)}
      width={720}
      className="custom-modal"
      centered
      styles={{
        header: { borderBottom: '3px solid transparent', borderImage: 'linear-gradient(90deg, #FF8800, #FF5E00, #4776E6, #0B1F4D) 1', paddingBottom: 14 },
      }}
      footer={
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 0 4px 0', borderTop: '1.5px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Brand Sync Active</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => handleClose(false)}
              disabled={submitting}
              style={{
                height: 38, padding: '0 20px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', background: '#fff',
                fontSize: 13, fontWeight: 700, color: '#64748b',
                cursor: 'pointer',
              }}
            >
              CANCEL
            </button>
            <button
              onClick={() => form.submit()}
              disabled={submitting}
              style={{
                height: 38, padding: '0 28px', borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)',
                fontSize: 13, fontWeight: 800, color: '#fff',
                cursor: 'pointer', letterSpacing: '0.3px',
                display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: '0 4px 14px rgba(255,136,0,0.3)',
              }}
            >
              {isEditing ? "UPDATE" : "CREATE"}
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        size="middle"
        requiredMark={false}
        onFinish={handleSubmit}
        onFinishFailed={() => message.error("Please fill required fields")}
        onValuesChange={(changedValues) => {
          if (changedValues.imageUrl !== undefined) {
            setImagePreview(changedValues.imageUrl);
          }
        }}
      >
        {/* ── Top: 2-Column Grid ──────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start', marginBottom: 12 }}>
          
          {/* ── LEFT COLUMN: Name & Description ── */}
          <div>
            {/* Brand Name */}
            <div style={{ marginBottom: 12 }}>
              <span style={labelStyle}>Brand Name <span style={{ color: '#ef4444' }}>*</span></span>
              <Form.Item name="name" noStyle rules={[{ required: true, message: "Brand name is required" }]}>
                <Input
                  placeholder="Enter brand name"
                  style={{ height: 36, borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13 }}
                />
              </Form.Item>
            </div>

            {/* Brand Description */}
            <div>
              <span style={labelStyle}>Brand Description</span>
              <Form.Item name="description" noStyle>
                <Input.TextArea
                  rows={3}
                  placeholder="Enter brand description"
                  style={{ borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, resize: 'none' }}
                />
              </Form.Item>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Image Upload / Preview ── */}
          <div>
            {/* Image Source Type */}
            <div style={{ marginBottom: 10 }}>
              <span style={labelStyle}>Image Source Type</span>
              <Radio.Group
                value={inputType}
                onChange={(e) => {
                  setInputType(e.target.value);
                  const currentVal = form.getFieldValue("imageUrl");
                  if (e.target.value === 'url' && currentVal && String(currentVal).startsWith('data:')) {
                    form.setFieldsValue({ imageUrl: '' });
                    setImagePreview('');
                  }
                }}
                style={{ display: 'flex', gap: 16 }}
              >
                <Radio value="upload">Upload Image</Radio>
                <Radio value="url">Use Image URL</Radio>
              </Radio.Group>
            </div>

            {/* Brand Logo Upload or Input */}
            {inputType === 'upload' ? (
              <div style={{ marginBottom: 24 }}>
                <Form.Item name="imageUrl" rules={[{ required: !isEditing, message: "Please upload brand logo" }]} noStyle>
                  <Input type="hidden" />
                </Form.Item>
                <Upload
                  className="upload-full-width"
                  beforeUpload={handleLogoUpload}
                  showUploadList={false}
                  accept="image/*"
                  style={{ width: '100%' }}
                >
                  <div style={{ width: '100%' }}>
                    {isUploadingImage ? (
                      <div style={{ padding: 60, textAlign: 'center', background: '#f8fafc', borderRadius: 12, border: '2px dashed #cbd5e1' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 10, color: '#64748b', fontSize: 13, fontWeight: 500 }}>Uploading Image...</div>
                      </div>
                    ) : imagePreview ? (
                      <div className="banner-image-preview" style={{
                        width: '100%', height: 260, borderRadius: 12, overflow: 'hidden',
                        border: '2px dashed #c7d2fe', cursor: 'pointer', position: 'relative'
                      }}>
                        <SafeImage src={imagePreview} alt="Brand Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        <div className="banner-overlay">
                          <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>Click to Change</span>
                        </div>
                      </div>
                    ) : (
                      <div className="banner-upload-placeholder" style={{
                        padding: '60px 20px', textAlign: 'center', background: '#f8fafc', borderRadius: 12,
                        border: '2px dashed #cbd5e1', cursor: 'pointer', transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          width: 48, height: 48, background: '#e0e7ff', borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
                        }}>
                          <UploadOutlined style={{ fontSize: 24, color: '#4f46e5' }} />
                        </div>
                        <div style={{ color: '#334155', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                          Upload Brand Image
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 12 }}>
                          PNG, JPG, WebP up to 3MB
                        </div>
                      </div>
                    )}
                  </div>
                </Upload>
                <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                  Maximum upload size: <strong>3MB</strong> (JPG, PNG, WEBP)
                </div>
                {imageError && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#ef4444', textAlign: 'center', background: '#fef2f2', padding: '8px', borderRadius: '6px', border: '1px solid #fca5a5' }}>
                    {imageError}
                  </div>
                )}
              </div>
            ) : (
              <>
                <Form.Item name="imageUrl" noStyle rules={[{ required: !isEditing, message: "Brand logo URL is required" }]}>
                  <Input
                    placeholder="https://example.com/logo.jpg"
                    onChange={(e) => {
                      setImagePreview(e.target.value);
                      form.setFieldsValue({ imageUrl: e.target.value });
                    }}
                    style={{ height: 36, borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 12, marginBottom: 8 }}
                  />
                </Form.Item>
                {imagePreview && (
                  <div style={{ borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2e8f0', height: 86, background: '#fff' }}>
                    <SafeImage
                      src={imagePreview}
                      alt="Brand Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Bottom: Products Section ── */}
        <div>
          <span style={labelStyle}>Associated Products</span>
          <Form.List name="products">
            {(fields, { add, remove }) => (
              <>
                <div style={{ maxHeight: 150, overflowY: 'auto', paddingRight: 4, marginBottom: 8 }}>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{
                      marginBottom: 6, padding: '8px 12px', border: '1px solid #f1f5f9',
                      borderRadius: 10, background: '#f8fafc', boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                    }}>
                      <Row gutter={12} align="middle">
                        <Col span={10}>
                          <Form.Item
                            {...restField}
                            name={[name, "productId"]}
                            noStyle
                            rules={[{ required: true, message: "Required" }]}
                          >
                            <Select
                              placeholder="Select Product"
                              showSearch
                              optionFilterProp="children"
                              style={{ width: '100%' }}
                            >
                              {productList.map((prod) => (
                                <Option key={prod.id || prod._id} value={prod.id || prod._id}>
                                  {prod.name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "description"]}
                            noStyle
                          >
                            <Input
                              placeholder="Describe association role (optional)..."
                              style={{ borderRadius: 8, height: 32, fontSize: 12 }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={2} style={{ textAlign: 'right' }}>
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined style={{ fontSize: 16 }} />}
                            onClick={() => remove(name)}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={{
                      height: 36, borderRadius: 10, border: '1.5px dashed #4776E6',
                      color: '#4776E6', fontWeight: 700, fontSize: 13, background: 'rgba(71,118,230,0.02)',
                    }}
                  >
                    Add Product Association
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </div>
      </Form>
    </Modal>
  );
};

export default AddBrands;
